import { safeParseFloat, safeParseInt } from "encamp-shared/src/utils/safeParseNumber";
import { Issue } from "generated-graphql/graphql";

import {
  ExtractionDraftValue,
  ExtractionFieldConfidence,
  ExtractionFieldSpec,
  ExtractionFieldValue,
  ExtractionRecordValues,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
  ExtractionRowGroupSpec,
  ExtractionRowOrigin,
  TRI_BOOLEAN_OPTIONS,
  fromDraftValue,
  isBlankDraft,
  toDraftValue,
} from "./types";

/**
 * One repeated row in the form. `extractedIndex` is the row's position in the
 * extraction result — null on a row the reviewer added — and travels with the
 * row so its confidence stays anchored to the cell the extractor produced, not
 * to wherever the row ends up after rows are added or deleted. It sits beside
 * the values rather than among them so a column key can never collide with it,
 * and it reaches the caller for the same reason it exists here: see
 * `ExtractionReviewedRow`.
 */
export interface ExtractionReviewRow {
  extractedIndex: number | null;
  /** Where the row came from — see `ExtractionRowOrigin`. */
  origin: ExtractionRowOrigin;
  values: Record<string, ExtractionDraftValue>;
}

/**
 * The react-hook-form shape behind the grid. Values are keyed by the caller's
 * own field keys, so a form path is `values.<fieldKey>` or
 * `rows.<groupKey>.<index>.values.<columnKey>`.
 */
export interface ExtractionReviewFormData {
  values: Record<string, ExtractionDraftValue>;
  rows: Record<string, ExtractionReviewRow[]>;
}

/** Whatever `useWatch` hands back mid-edit, which is deeply partial. */
export interface PartialExtractionReviewRow {
  extractedIndex?: number | null;
  origin?: ExtractionRowOrigin;
  values?: Record<string, ExtractionDraftValue | undefined>;
}

export interface PartialExtractionReviewFormData {
  values?: Record<string, ExtractionDraftValue | undefined>;
  rows?: Record<string, PartialExtractionReviewRow[] | undefined>;
}

/**
 * A field key becomes a react-hook-form path segment, so a key containing a dot
 * would address a nested object that the flat value record doesn't have. Callers
 * get a dev-time warning rather than a silently blank field.
 */
export function warnOnUnsupportedKeys(schema: ExtractionReviewSchema): void {
  const keys = [
    ...schema.sections.flatMap((section) => section.fields.map((field) => field.key)),
    ...(schema.rowGroups ?? []).flatMap((group) => [
      group.key,
      ...group.columns.map((column) => column.key),
    ]),
  ];
  const dotted = keys.filter((key) => key.includes("."));
  if (dotted.length > 0) {
    console.warn(
      `ExtractionReviewGrid: field keys cannot contain "." — these will not round-trip: ${dotted.join(", ")}`
    );
  }
}

/** Form path of a single-value field. */
export function fieldPath(fieldKey: string): `values.${string}` {
  return `values.${fieldKey}`;
}

/** Form path of a repeated group's array. */
export function rowGroupPath(groupKey: string): `rows.${string}` {
  return `rows.${groupKey}`;
}

/** Form path of one cell of a repeated row. */
export function rowCellPath(groupKey: string, index: number, columnKey: string): `rows.${string}` {
  return `rows.${groupKey}.${index}.values.${columnKey}`;
}

/**
 * A stable key for one cell of a repeated row, for anything that has to outlive
 * the list's own numbering.
 *
 * Deliberately not the form path, which addresses a control by position:
 * `rows.constituents.2.values.casNumber` means a different constituent the moment
 * a row above it is removed. Anything the reviewer decided about a cell has to
 * follow the cell, which is what `extractedIndex` is for — see
 * `ExtractionReviewedRow`. A cell the extractor never proposed has no index and
 * nothing to decide, since neither flag can be raised on it.
 */
export function rowCellKey(groupKey: string, extractedIndex: number, columnKey: string): string {
  return `${groupKey}:${extractedIndex}:${columnKey}`;
}

/**
 * Form path of one whole row. Not a control the reviewer edits — it addresses the
 * row's own box, which is what a question about the row rather than about one of
 * its values has to travel to.
 */
export function rowPath(groupKey: string, index: number): `rows.${string}` {
  return `rows.${groupKey}.${index}`;
}

/**
 * Seed the form from an extraction, leaving fields the extractor missed empty.
 *
 * A record the reading has no counterpart for is seeded too: its rows are
 * appended after the extracted ones so confirming a review cannot delete a row
 * the reviewer was never shown. They carry no `extractedIndex`, because the model
 * produced no reading of them — see `ExtractionRowOrigin`.
 */
export function buildDefaultValues(
  schema: ExtractionReviewSchema,
  extraction: ExtractionResult | null | undefined,
  record?: ExtractionRecordValues
): ExtractionReviewFormData {
  const values: Record<string, ExtractionDraftValue> = {};
  for (const section of schema.sections) {
    for (const field of section.fields) {
      values[field.key] = toDraftValue(field, extraction?.values?.[field.key]);
    }
  }

  const rows: Record<string, ExtractionReviewRow[]> = {};
  for (const group of schema.rowGroups ?? []) {
    const extractedRows = extraction?.rows?.[group.key] ?? [];
    const extracted: ExtractionReviewRow[] = extractedRows.map((extractedRow, extractedIndex) => ({
      extractedIndex,
      origin: "extraction",
      values: toRowDrafts(group, extractedRow),
    }));
    const { unmatched } = pairRecordRows(group, extractedRows, record?.rows?.[group.key]);
    rows[group.key] = [
      ...extracted,
      ...unmatched.map((recordRow) => ({
        extractedIndex: null,
        origin: "record" as const,
        values: toRowDrafts(group, recordRow),
      })),
    ];
  }

  return { values, rows };
}

function toRowDrafts(
  group: ExtractionRowGroupSpec,
  row: Readonly<Record<string, ExtractionFieldValue>>
): Record<string, ExtractionDraftValue> {
  return Object.fromEntries(
    group.columns.map((column) => [column.key, toDraftValue(column, row[column.key])])
  );
}

/** A blank repeated row, which has no extracted candidate behind it. */
export function buildBlankRow(columnKeys: readonly string[]): ExtractionReviewRow {
  return {
    extractedIndex: null,
    origin: "added",
    values: Object.fromEntries(columnKeys.map((key) => [key, null])),
  };
}

/**
 * Which record row each extracted row stands for, and which ones the reading
 * left out entirely.
 *
 * Paired on the group's `identityColumns` rather than on the whole row, because
 * the interesting case is the same thing valued differently: a constituent the
 * sheet restates at a new percentage is a change to flag on the cell, not a row
 * the record does not have. Matching is greedy, so a record listing one chemical
 * twice pairs each occurrence with a separate extracted row.
 *
 * A record row with nothing in it is dropped rather than surfaced: there is
 * nothing for the reviewer to keep, and an empty row would block confirm on a
 * grid that otherwise holds values.
 */
export function pairRecordRows(
  group: ExtractionRowGroupSpec,
  extractedRows: readonly Readonly<Record<string, ExtractionFieldValue>>[] | undefined,
  recordRows: readonly Readonly<Record<string, ExtractionFieldValue>>[] | undefined
): {
  byExtractedIndex: Map<number, Readonly<Record<string, ExtractionFieldValue>>>;
  unmatched: Readonly<Record<string, ExtractionFieldValue>>[];
} {
  const byExtractedIndex = new Map<number, Readonly<Record<string, ExtractionFieldValue>>>();
  const unmatched: Readonly<Record<string, ExtractionFieldValue>>[] = [];
  if (!recordRows || recordRows.length === 0) return { byExtractedIndex, unmatched };

  const available = new Map<string, number[]>();
  (extractedRows ?? []).forEach((extractedRow, index) => {
    const key = rowIdentityKey(group, extractedRow);
    if (key == null) return;
    const queue = available.get(key);
    if (queue) queue.push(index);
    else available.set(key, [index]);
  });

  for (const recordRow of recordRows) {
    if (isBlankRow(group, recordRow)) continue;
    const key = rowIdentityKey(group, recordRow);
    const queue = key == null ? undefined : available.get(key);
    const matchedIndex = queue?.shift();
    if (matchedIndex == null) unmatched.push(recordRow);
    else byExtractedIndex.set(matchedIndex, recordRow);
  }

  return { byExtractedIndex, unmatched };
}

/**
 * How an answer about a whole row is remembered.
 *
 * A record-only row has no `extractedIndex` to key by — the model never proposed
 * it — and its position renumbers as rows go, so neither works. Its identity
 * columns do: the same normalization the pairing uses, so the key follows the row
 * the reviewer answered about even after rows above it are removed. Editing the
 * row's identity loses the answer, which is right — it is a different row now.
 */
export function rowAnswerKey(
  group: ExtractionRowGroupSpec,
  rows: readonly (
    | { origin?: ExtractionRowOrigin; values?: Record<string, ExtractionDraftValue | undefined> }
    | undefined
  )[],
  index: number
): string {
  const identity = rowIdentity(group, rows[index]?.values ?? {});
  // Two record rows can be identical — the same chemical listed twice — and the
  // identity alone would give them one key, so dismissing either would dismiss
  // both. Counting the identical ones before this one separates them, and unlike
  // the row's own position it only shifts when one of *those* rows goes.
  let occurrence = 0;
  for (let before = 0; before < index; before += 1) {
    const other = rows[before];
    if (other?.origin !== "record") continue;
    if (rowIdentity(group, other.values ?? {}) === identity) occurrence += 1;
  }
  return `${group.key}:row:${identity}:${occurrence}`;
}

/**
 * What to call one repeated row where it is named away from its own box — a line
 * in the attention list, a warning that names it.
 *
 * The identity columns are what says which row this is — a constituent's name, a
 * shipping line's description — so the first of them holding anything is the
 * row's name. Its position is the fallback and not the default: "constituent 3"
 * makes the reviewer count boxes to find which constituent is meant, and the row
 * itself already carries that number at the top of its box.
 */
export function rowDisplayName(
  group: ExtractionRowGroupSpec,
  values: Record<string, ExtractionDraftValue | undefined> | undefined,
  index: number
): string {
  for (const column of identityColumnSpecs(group)) {
    const draft = values?.[column.key];
    if (isBlankDraft(draft)) continue;
    return displayValue(column, draft);
  }
  return `${group.rowNoun} ${index + 1}`;
}

function rowIdentity(
  group: ExtractionRowGroupSpec,
  values: Record<string, ExtractionDraftValue | undefined>
): string {
  return identityColumnSpecs(group)
    .map((column) => {
      const draft = values[column.key];
      if (typeof draft === "boolean") return draft ? "true" : "false";
      return draft == null ? "" : draft.trim().toLowerCase();
    })
    .join("\u0000");
}

/**
 * A row's identity as one comparable string, or null when the identity columns
 * hold nothing to compare — an unidentifiable row is never paired, since a blank
 * key would match every other blank one.
 */
function rowIdentityKey(
  group: ExtractionRowGroupSpec,
  row: Readonly<Record<string, ExtractionFieldValue>>
): string | null {
  const columns = identityColumnSpecs(group);
  const parts = columns.map((column) => normalizeIdentity(column, row[column.key]));
  if (parts.every((part) => part === "")) return null;
  // Column-delimited so ("AB", "C") and ("A", "BC") are different identities.
  return parts.join("\u0000");
}

function identityColumnSpecs(group: ExtractionRowGroupSpec): readonly ExtractionFieldSpec[] {
  const keys = group.identityColumns;
  if (keys == null) return group.columns;
  const specs = group.columns.filter((column) => keys.includes(column.key));
  // A group naming identity columns the schema does not have would otherwise
  // compare nothing and pair every row with every other.
  return specs.length > 0 ? specs : group.columns;
}

/** Case- and format-insensitive, since the two sides are stored separately. */
function normalizeIdentity(
  spec: ExtractionFieldSpec,
  value: ExtractionFieldValue | undefined
): string {
  const draft = toDraftValue(spec, value);
  if (typeof draft === "boolean") return draft ? "true" : "false";
  if (draft == null) return "";
  if (spec.kind === "number") {
    const parsed = safeParseFloat(draft);
    return parsed == null ? draft.trim().toLowerCase() : String(parsed);
  }
  return draft.trim().toLowerCase();
}

function isBlankRow(
  group: ExtractionRowGroupSpec,
  row: Readonly<Record<string, ExtractionFieldValue>>
): boolean {
  return group.columns.every((column) => isBlankDraft(toDraftValue(column, row[column.key])));
}

/** Convert the reviewed drafts back to the caller's value types. */
export function toReviewedValues(
  schema: ExtractionReviewSchema,
  data: ExtractionReviewFormData
): ExtractionReviewValues {
  const values: ExtractionReviewValues["values"] = {};
  for (const section of schema.sections) {
    for (const field of section.fields) {
      values[field.key] = fromDraftValue(field, data.values?.[field.key]);
    }
  }

  const rows: ExtractionReviewValues["rows"] = {};
  for (const group of schema.rowGroups ?? []) {
    rows[group.key] = (data.rows?.[group.key] ?? []).map((row) => ({
      extractedIndex: row.extractedIndex ?? null,
      values: Object.fromEntries(
        group.columns.map((column) => [
          column.key,
          fromDraftValue(column, row.values?.[column.key]),
        ])
      ),
    }));
  }

  return { values, rows };
}

/** True when the reviewer has left nothing that could be committed. */
export function isEmptyReview(data: PartialExtractionReviewFormData): boolean {
  if (Object.values(data.values ?? {}).some((value) => !isBlankDraft(value))) return false;
  return !Object.values(data.rows ?? {}).some((rows) =>
    (rows ?? []).some((row) =>
      Object.values(row?.values ?? {}).some((value) => !isBlankDraft(value))
    )
  );
}

export function indexConfidence(
  confidence: readonly ExtractionFieldConfidence[] | undefined
): Map<string, number> {
  const index = new Map<string, number>();
  for (const entry of confidence ?? []) index.set(entry.field, entry.score);
  return index;
}

/**
 * Whether two drafts hold the same thing. A value the extractor left empty and
 * one the reviewer typed into and then cleared are the same state, so null,
 * empty string, and whitespace all compare equal.
 */
export function draftsEqual(a: ExtractionDraftValue, b: ExtractionDraftValue): boolean {
  if (typeof a === "boolean" || typeof b === "boolean") return a === b;
  const left = a == null ? "" : a.trim();
  const right = b == null ? "" : b.trim();
  return left === right;
}

/**
 * Has the reviewer supplied something other than what the extractor proposed?
 *
 * Derived by comparing the two values rather than read from react-hook-form's
 * `dirtyFields`, deliberately. RHF recomputes dirty state positionally against
 * the original defaults, so removing a repeated row marks every surviving row
 * cell dirty even though nobody touched them; and its comparison is strict, so
 * clearing a field the extractor already left empty would count as an edit. Both
 * would wrongly retire a "needs review" flag. A comparison against the value's
 * own extracted candidate can't drift in either way.
 */
export function isReviewed(
  spec: ExtractionFieldSpec,
  current: ExtractionDraftValue | undefined,
  extracted: ExtractionDraftValue
): boolean {
  return valuesDiffer(spec, current, extracted);
}

/**
 * Do two drafts of the same field hold different readings?
 *
 * A number is compared as a number: 5 and "5.0" are the same reading, and
 * treating them as different both retires a low-confidence flag and pollutes the
 * per-field edit signal the extraction pipeline records for tuning.
 */
export function valuesDiffer(
  spec: ExtractionFieldSpec,
  current: ExtractionDraftValue | undefined,
  other: ExtractionDraftValue
): boolean {
  if (spec.kind === "number") {
    const left = typeof current === "string" ? safeParseFloat(current) : null;
    const right = typeof other === "string" ? safeParseFloat(other) : null;
    if (left != null || right != null) return left !== right;
  }
  return !draftsEqual(current ?? null, other);
}

/** True while a value is still an unreviewed low-confidence extraction. */
export function isFlagged(
  score: number | null,
  reviewed: boolean,
  lowConfidenceThreshold: number
): boolean {
  if (reviewed || score == null) return false;
  return score < lowConfidenceThreshold;
}

/**
 * Is this value about to replace something else the record holds?
 *
 * True while the form still shows the extractor's untouched reading and the
 * record holds a different, non-empty value — the state where confirming
 * overwrites something the reviewer has not been shown. An edit clears it, the
 * same way an edit clears a low-confidence flag: the reviewer has now seen both
 * values and answered, whichever one they kept.
 *
 * A record value that is blank — including a `false` checkbox, which is
 * indistinguishable from a column nobody filled in — is nothing to lose, so it
 * is never flagged. Neither is a row the reading never proposed: a record row is
 * called out as a row, and a row the reviewer added has no record behind it.
 */
export function isRecordConflict({
  spec,
  current,
  extracted,
  recordValue,
  hasCandidate,
}: {
  spec: ExtractionFieldSpec;
  current: ExtractionDraftValue | undefined;
  extracted: ExtractionDraftValue;
  recordValue: ExtractionDraftValue | undefined;
  hasCandidate: boolean;
}): boolean {
  if (!hasCandidate || recordValue === undefined || isBlankDraft(recordValue)) return false;
  if (!valuesDiffer(spec, current, recordValue)) return false;
  return !isReviewed(spec, current, extracted);
}

/**
 * The record's values as drafts, so a conflict compares like with like. A key the
 * caller did not supply stays absent — "the record says nothing here" is not the
 * same claim as "the record is empty here", and only the first should go
 * unflagged when the reading fills the field in.
 */
/**
 * The fields a save would actually change on the record.
 *
 * The question `hasWork` cannot answer. `hasWork` compares the form against the
 * extraction *seed* — "has the reviewer diverged from what the AI proposed" —
 * which is the wrong question when the reviewer agrees with the AI. Agreeing
 * requires no keystroke, so `hasWork` stays false, and closing then discards a
 * reading the reviewer had accepted with nothing to warn them.
 *
 * This compares the form against the record instead, which is what a save would
 * write over. A field is pending while the two differ, whatever the reviewer has
 * or has not touched.
 *
 * Fields the caller supplied no record value for are skipped rather than counted:
 * "the record says nothing here" is not the same as a difference, and counting it
 * would make every blank column on a fresh record read as pending work.
 */
export function pendingAgainstRecord(
  specs: readonly ExtractionFieldSpec[],
  data: PartialExtractionReviewFormData,
  recordValues: ReadonlyMap<string, ExtractionDraftValue>
): string[] {
  const pending: string[] = [];
  for (const spec of specs) {
    const recordValue = recordValues.get(spec.key);
    if (recordValue === undefined) continue;
    const current = data.values?.[spec.key];
    if (valuesDiffer(spec, current, recordValue)) pending.push(spec.key);
  }
  return pending;
}

export function toRecordDrafts(
  specs: readonly ExtractionFieldSpec[],
  values: Readonly<Record<string, ExtractionFieldValue>> | undefined
): Map<string, ExtractionDraftValue> {
  const drafts = new Map<string, ExtractionDraftValue>();
  if (!values) return drafts;
  for (const spec of specs) {
    if (!(spec.key in values)) continue;
    drafts.set(spec.key, toDraftValue(spec, values[spec.key]));
  }
  return drafts;
}

/** Every single-value field in a schema, in the order the grid lays them out. */
export function schemaFields(schema: ExtractionReviewSchema): readonly ExtractionFieldSpec[] {
  return schema.sections.flatMap((section) => section.fields);
}

/** How a draft reads back to the reviewer in a helper line, tooltip, or list. */
/**
 * A value as the field itself shows it.
 *
 * A select stores an option's value and displays its label, and the two are
 * routinely different words: a tri-state holds `"true"` and shows "Yes", a
 * physical state holds `LIQUID` and shows "Liquid". Rendering the stored value
 * put "Saved true" beside a dropdown reading "Yes", and asked the reviewer to
 * work out that those are the same answer.
 *
 * Falls through to `displayDraft` for everything else, and for an option the list
 * does not carry — an extracted value outside the option set is shown as it is,
 * which is the same thing the control does with it.
 */
export function displayValue(
  spec: ExtractionFieldSpec,
  draft: ExtractionDraftValue | undefined
): string {
  const options = spec.kind === "triBoolean" ? TRI_BOOLEAN_OPTIONS : spec.options;
  if (options != null && typeof draft === "string") {
    const option = options.find((candidate) => candidate.value === draft);
    if (option != null) return option.label;
  }
  return displayDraft(draft);
}

export function displayDraft(draft: ExtractionDraftValue | undefined): string {
  if (draft === true) return "Yes";
  if (draft === false) return "No";
  if (draft == null || draft.trim() === "") return "(empty)";
  return draft;
}

/**
 * Re-key a caller's validation issues onto the form's paths.
 *
 * The caller validates in its own namespace — `"phLow"`, or a row cell — because
 * it has no reason to know how the grid nests its form. This bridges the two so
 * an issue lands on the right input, the same way `reviewFieldDiffs` bridges the
 * manifest comparators' field names to the manifest review form.
 *
 * Both row-cell forms are accepted, because the two systems that produce issues
 * number their lists differently and each is right in its own terms: the
 * compliance engine uses `constituents.0.maxPercent`, matching how
 * react-hook-form addresses a field array, while the extraction pipeline uses
 * `constituents[0].casNumber`, matching how its field paths index a stored JSON
 * payload.
 *
 * An issue whose key matches nothing in the schema keeps its key, so it still
 * reaches the issue list rather than disappearing.
 */
export function toFormIssues(schema: ExtractionReviewSchema, issues: readonly Issue[]): Issue[] {
  const rowGroupKeys = new Set((schema.rowGroups ?? []).map((group) => group.key));
  const fieldKeys = new Set(
    schema.sections.flatMap((section) => section.fields.map((field) => field.key))
  );

  return issues.map((issue) => {
    const key = issue.key;
    if (key == null) return issue;
    if (fieldKeys.has(key)) return { ...issue, key: fieldPath(key) };

    const indexed =
      /^([^.[\]]+)\[(\d+)\]\.(.+)$/.exec(key) ?? /^([^.[\]]+)\.(\d+)\.(.+)$/.exec(key);
    if (indexed != null && rowGroupKeys.has(indexed[1])) {
      return { ...issue, key: rowCellPath(indexed[1], safeParseInt(indexed[2]) ?? 0, indexed[3]) };
    }
    return issue;
  });
}
